"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import WordExpandableText from "@components/atoms/WordExpandableText";
import { SellCarSeoRichCard } from "@interfaces/sellCarSeo.types";
import { useIsMobile } from "@hooks/use-mobile";
import assetLoader from "@utils/internalImageLoder";
import styles from "./SellCarArticleCarousel.module.scss";
import { useGlobalLoader } from "@hooks/useGlobalLoader";
import { useRouter } from "next/navigation";

interface SellCarArticleCarouselProps {
  title: string;
  items: SellCarSeoRichCard[];
}

const DESKTOP_VISIBLE_COUNT = 3;
const MOBILE_VISIBLE_COUNT = 1;

export default function SellCarArticleCarousel({
  title,
  items,
}: SellCarArticleCarouselProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const isMobile = useIsMobile();
  const {showLoader} = useGlobalLoader();
  const router = useRouter();
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Array<HTMLElement | null>>([]);
  const scrollTargetIndexRef = useRef<number | null>(null);
  const scrollReleaseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [startIndex, setStartIndex] = useState(0);

  const visibleCount = isMobile ? MOBILE_VISIBLE_COUNT : DESKTOP_VISIBLE_COUNT;
  const canScroll = items.length > visibleCount;
  const maxStartIndex = Math.max(0, items.length - visibleCount);
  const displayCount = Math.min(startIndex + visibleCount, items.length);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const syncIndexFromScroll = () => {
    if (!scrollerRef.current || itemRefs.current.length === 0) return;

    if (!isMobile && scrollTargetIndexRef.current !== null) {
      setStartIndex(scrollTargetIndexRef.current);
      return;
    }

    const scrollLeft = scrollerRef.current.scrollLeft;
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    itemRefs.current.forEach((item, index) => {
      if (!item) return;
      const distance = Math.abs(item.offsetLeft - scrollLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    setStartIndex(Math.min(closestIndex, maxStartIndex));
  };

  useEffect(() => {
    syncIndexFromScroll();
  }, [isMobile]);

  useEffect(() => {
    return () => {
      if (scrollReleaseTimerRef.current) {
        clearTimeout(scrollReleaseTimerRef.current);
      }
    };
  }, []);

  const scrollToIndex = (index: number) => {
    const safeIndex = Math.max(0, Math.min(index, maxStartIndex));
    const target = itemRefs.current[safeIndex];

    if (!target) return;

    if (!isMobile) {
      scrollTargetIndexRef.current = safeIndex;
      if (scrollReleaseTimerRef.current) {
        clearTimeout(scrollReleaseTimerRef.current);
      }
      scrollReleaseTimerRef.current = setTimeout(() => {
        scrollTargetIndexRef.current = null;
        syncIndexFromScroll();
      }, 450);
    }

    target.scrollIntoView({
      behavior: "smooth",
      inline: "start",
      block: "nearest",
    });

    setStartIndex(safeIndex);
  };

  const handleNext = () => {
    const step = isMobile ? 1 : DESKTOP_VISIBLE_COUNT;
    scrollToIndex(Math.min(startIndex + step, maxStartIndex));
  };

  const handlePrev = () => {
    const step = isMobile ? 1 : DESKTOP_VISIBLE_COUNT;
    scrollToIndex(Math.max(startIndex - step, 0));
  };

  return (
    <div className={styles.section}>
      <div className={styles.headerRow}>
        <h2 className={styles.title}>{title}</h2>
        {isHydrated && !isMobile && canScroll ? (
          <div className={styles.controls}>
            <div className={styles.counter}>{`${displayCount}/${items.length}`}</div>
            <div className={styles.buttonRow}>
              <button
                type="button"
                className={styles.navButton}
                onClick={handlePrev}
                disabled={startIndex === 0}
                aria-label="Previous articles"
              >
                <Image
                  src={`${assetLoader()}/icons/Carousal/ArrowLeftDark.svg`}
                  alt=""
                  width={20}
                  height={20}
                />
              </button>
              <button
                type="button"
                className={styles.navButton}
                onClick={handleNext}
                disabled={startIndex >= maxStartIndex}
                aria-label="Next articles"
              >
                <Image
                  src={`${assetLoader()}/icons/Carousal/ArrowRightDark.svg`}
                  alt=""
                  width={20}
                  height={20}
                />
              </button>
            </div>
          </div>
        ) : null}
      </div>

      <div ref={scrollerRef} className={styles.scroller} onScroll={syncIndexFromScroll}>
        {items.map((item, index) => (
          <article
            key={`${item.title}-${index}`}
            ref={(node) => {
              itemRefs.current[index] = node;
            }}
            className={styles.card}
          >
            <h3 className={styles.cardTitle}>{item.title}</h3>
            {isHydrated ? (
              <WordExpandableText
                text={item.plainDescription}
                wordLimit={item.descriptionWordLimit || 200}
                className={styles.cardDescription}
                expandedClassName={styles.cardDescriptionExpanded}
                buttonClassName={styles.readMoreButton}
                onReadMoreClick={() => {
                  if (item?.link) {
                    showLoader();
                    router.push(item.link);
                  }
                }}
              />
            ) : (
              <p className={`${styles.cardDescription} ${styles.cardDescriptionExpanded}`}>
                {item.plainDescription}
              </p>
            )}
          </article>
        ))}
      </div>
    </div>
  );
}
